import { Flex, Text } from '@chakra-ui/react'
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import IncomingCallTone from '~/assets/sounds/incoming-call-waiting-ringtone.mp3'
import { Ringtone, useSettingsState } from '~/atoms/SettingsState'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import { MangoModal } from '~/components/shared/MangoModal'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useCallPopEnabled } from '~/hooks/useCallPopEnabled'
import { QueueAgentStatus, useCallQueue } from '~/hooks/useCallQueue'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import useElectronIPC from '~/hooks/useElectronIPC'
import { useMainWindow } from '~/hooks/useElectronWindow'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { Size } from '~/types/ThemeTypes'
import { noop } from '~/utils/functions'

import { toMaskedPhoneNumber } from '@mangovoice/core'
import { PhoneCallStatus } from '@mangovoice/types'

import { IncomingCallButtonVariants } from './componentStyles/IncomingCallButton.style'
import {
  IncomingCallModalVariants,
  useIncomingCallModalStyles,
} from './componentStyles/IncomingCallModal.style'
import { IncomingCallButton, ParkCallButton } from './IncomingCallButton'

export const IncomingCallModal = () => {
  const { focusMainWindow } = useMainWindow()
  const { t } = useTranslation()
  const effectiveCallPopEnabled = useCallPopEnabled()

  const [, setParkingLotShared] =
    useSharedVariable<TrackedParkingLot[]>('ParkingLots')

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        invite: { pendingInvitation, pendingInvitationUuid },
        isAtMaxCallLimit,
      },
      trackedParkingLots: parkingLots,
    },
    actions: {
      answerCall,
      answerCallWithHoldCurrent,
      answerCallWithEndCurrent,
      answerCallWithParkCurrent,
      rejectCall,
      rejectCallWithBusy,
      holdCall,
      endCall,
      endAllSoftphoneCalls,
      switchCall,
    },
  } = usePhoneEndpoint({})

  const { initHandlePhoneCallListener } = useElectronIPC()

  const {
    settings: {
      webPhone: { ringtone, doNotDisturbTimestamp, hasCallWaiting },
    },
  } = useSettingsState()
  const doNotDisturb: boolean = doNotDisturbTimestamp !== undefined
  const { currentAgentStatus } = useCallQueue()
  const isInQueue = currentAgentStatus === QueueAgentStatus.InQueue
  const [shouldRender, setShouldRender] = useState(false)
  const { logger } = useCloudwatchLogger('IncomingCallModal')

  // Track focus state to prevent accidental keypress triggers
  const previousActiveElementRef = useRef<HTMLElement | null>(null)
  const [wasUserTyping, setWasUserTyping] = useState(false)
  const [hasExplicitModalInteraction, setHasExplicitModalInteraction] =
    useState(false)
  const [gracePeriodElapsed, setGracePeriodElapsed] = useState(false)
  const modalContainerRef = useRef<HTMLDivElement>(null)
  const headerTextId = useId()
  const callerNameId = useId()
  const callerNumberId = useId()
  const lastKeyWasTabRef = useRef(false)
  const gracePeriodElapsedRef = useRef(false)
  const GRACE_PERIOD_MS = 500 // Half a second grace period
  // The invitation object is replaced whenever the ringing call is updated, so
  // focus/grace-period effects key on its UUID to avoid restarting mid-ring.
  const ringingCallUuid = pendingInvitation
    ? pendingInvitation.callUuid || pendingInvitationUuid || 'pending'
    : null

  // Capture active element synchronously when pendingInvitation appears (useLayoutEffect runs before useEffect)
  // This ensures we capture it before focusMainWindow() is called
  useLayoutEffect(() => {
    if (ringingCallUuid) {
      // If we don't have a previous element, capture current one
      if (!previousActiveElementRef.current) {
        const activeEl = document.activeElement as HTMLElement
        previousActiveElementRef.current = activeEl
        const isInput =
          activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA'
        setWasUserTyping(isInput)
      } else {
        // If we have a previous element, check if it was an input
        // This handles the case where focus was lost but we want to preserve typing state
        const prevEl = previousActiveElementRef.current
        const wasInput =
          prevEl?.tagName === 'INPUT' || prevEl?.tagName === 'TEXTAREA'
        if (wasInput) {
          setWasUserTyping(true)
        }
      }
    }
  }, [ringingCallUuid])

  const handleRejectButtonClick = useCallback(() => {
    rejectCall()
    track(WebPhoneAnalyticsEvents.InviteRejectedClick)
  }, [rejectCall, track])

  const handleAcceptButtonClick = useCallback(async () => {
    track(WebPhoneAnalyticsEvents.ModalAnswerClicked)
    try {
      await answerCall(pendingInvitationUuid || undefined)
    } catch (error: any) {
      logger?.error('Failed to answer call', error, {
        pendingInvitationUuid,
      })
    }
  }, [answerCall, pendingInvitationUuid, logger, track])

  const handleParkCallButtonClick = useCallback(
    async (parkingLot: TrackedParkingLot, callUuid?: string) => {
      track(WebPhoneAnalyticsEvents.ModalParkAndAcceptClicked)
      try {
        await answerCallWithParkCurrent(parkingLot.targetNumber, callUuid)
      } catch (error: unknown) {
        logger?.error(
          'Failed to park current call and answer',
          error as Error,
          {
            pendingInvitationUuid,
            parkTarget: parkingLot.targetNumber,
          },
        )
      }
    },
    [answerCallWithParkCurrent, logger, pendingInvitationUuid, track],
  )

  const shouldRing = ringtone !== Ringtone.SILENT

  const hasNoActiveCall = !activeCall
  const mustRejectOrEndCall = activeCall !== undefined

  // Hold & Accept keeps the same number of calls (1 held + 1 connected)
  // so we don't need to check isAtMaxCallLimit here
  const canParkOrHoldCall =
    activeCall && activeCall.status === PhoneCallStatus.CONNECTED

  const variant = canParkOrHoldCall
    ? IncomingCallModalVariants.DoubleRow
    : IncomingCallModalVariants.SingleRow
  const styles = useIncomingCallModalStyles({ variant })

  // Log button visibility conditions for debugging
  useEffect(() => {
    if (pendingInvitation && shouldRender) {
      // Capture activeCall properties before type narrowing
      const activeCallData = activeCall
        ? {
            status: activeCall?.status,
            callUuid: activeCall?.callUuid,
            number: activeCall?.number,
            isOnHold: activeCall?.isOnHold,
          }
        : null

      // Determine which buttons will actually render
      const willShowBasicButtons = hasNoActiveCall
      const willShowEndAndAccept = mustRejectOrEndCall
      const willShowHoldAndAccept = canParkOrHoldCall
      const willShowAnyButtons =
        willShowBasicButtons || willShowEndAndAccept || willShowHoldAndAccept

      // Log all input values and computed conditions
      logger?.info('IncomingCallModal button visibility check', {
        pendingInvitationUuid,
        // Input values
        activeCallExists: !!activeCall,
        activeCallStatus: activeCallData?.status,
        activeCallUuid: activeCallData?.callUuid,
        activeCallNumber: activeCallData?.number,
        activeCallIsOnHold: activeCallData?.isOnHold,
        isAtMaxCallLimit,
        // Computed values
        hasNoActiveCall,
        mustRejectOrEndCall,
        canParkOrHoldCall,
        // Button rendering decisions
        willShowBasicButtons,
        willShowEndAndAccept,
        willShowHoldAndAccept,
        willShowAnyButtons,
      })

      // ERROR level log when NO buttons would render (bug condition)
      if (!willShowAnyButtons) {
        logger?.error(
          'IncomingCallModal rendering with NO buttons - BUG DETECTED',
          null,
          {
            pendingInvitationUuid,
            activeCallExists: !!activeCall,
            activeCallStatus: activeCallData?.status,
            activeCallUuid: activeCallData?.callUuid,
            activeCallNumber: activeCallData?.number,
            activeCallIsOnHold: activeCallData?.isOnHold,
            isAtMaxCallLimit,
            hasNoActiveCall,
            mustRejectOrEndCall,
            canParkOrHoldCall,
            // Additional context
            effectiveCallPopEnabled,
            doNotDisturb,
            hasCallWaiting,
            isInQueue,
          },
        )
      }
    }
  }, [
    pendingInvitation,
    shouldRender,
    pendingInvitationUuid,
    activeCall,
    isAtMaxCallLimit,
    hasNoActiveCall,
    mustRejectOrEndCall,
    canParkOrHoldCall,
    logger,
    effectiveCallPopEnabled,
    doNotDisturb,
    hasCallWaiting,
    isInQueue,
  ])

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const modalProps = {
    flexDirection: styles.modal?.flexDirection,
    gap: styles.modal?.gap,
  }

  const headerBlockProps = {
    flexDirection: styles.headerBlock?.flexDirection,
    alignItems: styles.headerBlock?.alignItems,
    justifyContent: styles.headerBlock?.justifyContent,
  }

  const buttonRowProps = {
    justifyContent: styles.buttonRow?.justifyContent,
    gap: styles.buttonRow?.gap,
  }

  // Create wrapper functions that use the specific call UUID from IPC
  const handleIPCAnswerCall = useCallback(
    async (callUuid?: string) => {
      try {
        await answerCall(callUuid || pendingInvitationUuid || undefined)
      } catch (error: any) {
        logger?.error('Failed to answer call via IPC', error, {
          callUuid: callUuid || pendingInvitationUuid,
        })
      }
    },
    [answerCall, pendingInvitationUuid, logger],
  )

  const handleIPCRejectCall = useCallback(
    (callUuid?: string) => {
      rejectCall(callUuid || pendingInvitationUuid || undefined)
      track(WebPhoneAnalyticsEvents.InviteRejectedClick)
    },
    [rejectCall, pendingInvitationUuid, track],
  )

  const handleIPCAnswerCallWithEnd = useCallback(
    async (callUuid?: string) => {
      try {
        await answerCallWithEndCurrent(
          callUuid || pendingInvitationUuid || undefined,
        )
      } catch (error: any) {
        logger?.error('Failed to answer call with end via IPC', error, {
          callUuid: callUuid || pendingInvitationUuid,
        })
      }
    },
    [answerCallWithEndCurrent, pendingInvitationUuid, logger],
  )

  const handleIPCAnswerCallWithHold = useCallback(
    async (callUuid?: string) => {
      try {
        await answerCallWithHoldCurrent(
          callUuid || pendingInvitationUuid || undefined,
        )
      } catch (error: any) {
        logger?.error('Failed to answer call with hold via IPC', error, {
          callUuid: callUuid || pendingInvitationUuid,
        })
      }
    },
    [answerCallWithHoldCurrent, pendingInvitationUuid, logger],
  )

  const handleIPCAnswerCallWithParkCurrent = useCallback(
    async (parkTarget: string, callUuid?: string) => {
      try {
        await answerCallWithParkCurrent(
          parkTarget,
          callUuid || pendingInvitationUuid || undefined,
        )
      } catch (error: unknown) {
        logger?.error(
          'Failed to answer call with park via IPC',
          error as Error,
          {
            parkTarget,
            callUuid: callUuid || pendingInvitationUuid,
          },
        )
      }
    },
    [answerCallWithParkCurrent, pendingInvitationUuid, logger],
  )

  const handleIPCRejectCallWithBusy = useCallback(
    (callUuid?: string) => {
      rejectCallWithBusy(callUuid || pendingInvitationUuid || undefined)
    },
    [rejectCallWithBusy, pendingInvitationUuid],
  )

  useEffect(() => {
    const removeListener = initHandlePhoneCallListener({
      answerCall: handleIPCAnswerCall,
      rejectCall: handleIPCRejectCall,
      answerCallWithEnd: handleIPCAnswerCallWithEnd,
      answerCallWithHold: handleIPCAnswerCallWithHold,
      answerCallWithParkCurrent: handleIPCAnswerCallWithParkCurrent,
      holdCall: (callUuid: string) => holdCall(callUuid, true),
      resumeCall: (callUuid: string) => holdCall(callUuid, false),
      endCall,
      endAllSoftphoneCalls,
      switchCall,
      parkingLots: parkingLots || [],
    })

    // Add listener for reject-call-with-busy from Popup
    window.electron?.receive(
      'popup-reject-call-with-busy',
      handleIPCRejectCallWithBusy,
    )

    return () => {
      removeListener?.()
      // Cleanup for reject-call-with-busy listener
      window.electron?.removeListener?.(
        'popup-reject-call-with-busy',
        handleIPCRejectCallWithBusy,
      )
    }
  }, [
    initHandlePhoneCallListener,
    handleIPCAnswerCall,
    handleIPCRejectCall,
    handleIPCAnswerCallWithEnd,
    handleIPCAnswerCallWithHold,
    handleIPCAnswerCallWithParkCurrent,
    handleIPCRejectCallWithBusy,
    holdCall,
    endCall,
    endAllSoftphoneCalls,
    switchCall,
    parkingLots,
  ])

  useEffect(() => {
    setParkingLotShared(parkingLots || [])
  }, [parkingLots, setParkingLotShared])

  // Track if user was typing when modal opens and preserve focus
  useEffect(() => {
    if (shouldRender && ringingCallUuid) {
      // Reset grace period state when modal opens
      setGracePeriodElapsed(false)

      // Use the captured active element from useLayoutEffect
      const activeEl = previousActiveElementRef.current
      const isInput =
        activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA'

      setHasExplicitModalInteraction(false)
      lastKeyWasTabRef.current = false
      gracePeriodElapsedRef.current = false

      // Set up timer to mark grace period as elapsed
      const gracePeriodTimer = setTimeout(() => {
        gracePeriodElapsedRef.current = true
        setGracePeriodElapsed(true)
      }, GRACE_PERIOD_MS)

      // If user was typing, restore focus after a brief delay (let modal render)
      if (isInput && activeEl) {
        // Use requestAnimationFrame to ensure modal has rendered
        requestAnimationFrame(() => {
          // Never pull focus back out once it is inside the dialog
          const currentActive = document.activeElement as HTMLElement | null
          const dialogElement =
            modalContainerRef.current?.closest('[role="dialog"]') ??
            modalContainerRef.current
          const modalContainsFocus = !!dialogElement?.contains(currentActive)
          if (!modalContainsFocus && activeEl) {
            activeEl.focus()
          }
        })
      }

      return () => {
        clearTimeout(gracePeriodTimer)
      }
    } else {
      // Reset state when modal closes
      // Don't reset previousActiveElementRef - we want to preserve it for the next modal open
      // This helps detect if user was typing even if focus was lost during modal rendering
      setHasExplicitModalInteraction(false)
      setGracePeriodElapsed(false)
      gracePeriodElapsedRef.current = false
      // Only reset typing state once the invitation is gone (modal fully
      // closed). This branch also runs while an invitation is pending but
      // shouldRender hasn't caught up yet, and resetting then would lose the
      // typing state captured by the layout effect.
      if (!ringingCallUuid) {
        setWasUserTyping(false)
        previousActiveElementRef.current = null
      }
    }
  }, [shouldRender, ringingCallUuid])

  // Tab is captured at the document level because it is pressed on the element
  // the user was typing in, before focus moves into the dialog. A Tab during
  // the grace period may be part of what the user was typing, so it doesn't
  // count.
  useEffect(() => {
    if (!shouldRender || !ringingCallUuid) {
      return
    }
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      lastKeyWasTabRef.current =
        event.key === 'Tab' && gracePeriodElapsedRef.current
    }
    const handleDocumentPointerDown = () => {
      lastKeyWasTabRef.current = false
    }
    document.addEventListener('keydown', handleDocumentKeyDown, true)
    document.addEventListener('pointerdown', handleDocumentPointerDown, true)
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown, true)
      document.removeEventListener(
        'pointerdown',
        handleDocumentPointerDown,
        true,
      )
    }
  }, [shouldRender, ringingCallUuid])

  // Programmatic focus (e.g. the dialog's initial focus) doesn't count;
  // only focus that arrives through Tab navigation does.
  const handleModalFocus = useCallback(() => {
    const focusCameFromTab = lastKeyWasTabRef.current
    lastKeyWasTabRef.current = false
    if (focusCameFromTab) {
      setHasExplicitModalInteraction(true)
    }
  }, [])

  const handleModalKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      // Arrow keys don't count: typists use them to move the text cursor, and
      // they don't move focus between these buttons.
      if (event.key === 'Tab' && gracePeriodElapsedRef.current) {
        setHasExplicitModalInteraction(true)
      }
    },
    [],
  )

  const handleModalMouseDown = useCallback(() => {
    setHasExplicitModalInteraction(true)
  }, [])

  // Enter/Space on the call buttons are ignored until the user deliberately
  // interacts with the dialog (pointer press, or Tab/Shift+Tab into or
  // within it), unless they weren't typing and the grace period has elapsed.
  const allowKeyboardActivation =
    hasExplicitModalInteraction || (!wasUserTyping && gracePeriodElapsed)

  useEffect(() => {
    // Check rejection conditions first, before setting shouldRender
    if (doNotDisturb && pendingInvitation) {
      rejectCall()
      track(WebPhoneAnalyticsEvents.InviteRejectedDND)
      setShouldRender(false)
      return
    }

    // Call Queue agents should never receive multiple QUEUE calls
    // But they CAN receive direct extension calls when "Respond with Busy" is OFF
    // Note: We can't reliably detect queue calls here (no cc_queue info in pendingInvitation)
    // So we rely on the WebSocket handler to reject second queue calls
    // This check is kept for backward compatibility but should not trigger for direct calls

    // Only reject based on hasCallWaiting setting for non-queue agents
    // Queue agents: Let WebSocket handler decide (it has cc_queue info)
    // Non-queue agents: Apply hasCallWaiting setting
    // hasCallWaiting misnomer: true = "Respond with Busy" ENABLED (reject second calls)
    if (hasCallWaiting && activeCall && pendingInvitation && !isInQueue) {
      rejectCallWithBusy()
      track(WebPhoneAnalyticsEvents.InviteRejectedBusy)
      setShouldRender(false)
      return
    }

    // Only render if there's a pending invitation and it won't be rejected
    setShouldRender(!!pendingInvitation)

    // Focus main window for non-popup scenarios
    if (pendingInvitation && !effectiveCallPopEnabled) {
      focusMainWindow()
    }
  }, [
    pendingInvitation,
    doNotDisturb,
    hasCallWaiting,
    activeCall,
    effectiveCallPopEnabled,
    focusMainWindow,
    rejectCall,
    rejectCallWithBusy,
    track,
    isInQueue,
  ])

  // Additional guard: don't render if call should be rejected
  // Queue agents: Don't reject based on hasCallWaiting (let WebSocket handler decide)
  const shouldRejectCall =
    (doNotDisturb && pendingInvitation) ||
    (hasCallWaiting && activeCall && pendingInvitation && !isInQueue)

  // Early return after IPC listener setup
  if (
    effectiveCallPopEnabled ||
    !shouldRender ||
    !pendingInvitation ||
    shouldRejectCall
  ) {
    return null
  }

  return (
    <MangoModal
      open={true}
      onOpenChange={noop}
      closeButton={false}
      headerDisabled={true}
      ariaLabelledBy={`${headerTextId} ${callerNameId} ${callerNumberId}`}
      size={Size.md}
    >
      <Flex
        ref={modalContainerRef}
        css={styles.modal}
        {...modalProps}
        onFocus={handleModalFocus}
        onKeyDown={handleModalKeyDown}
        onMouseDown={handleModalMouseDown}
      >
        <Flex css={styles.headerBlock} {...headerBlockProps}>
          <Text id={headerTextId} css={styles.headerText}>
            {t('incomingCallModal.newCallFrom')}
          </Text>
          <Text id={callerNameId} css={styles.callerName}>
            {pendingInvitation?.isPageOrIntercom
              ? t('popup.callItem.pageIntercom')
              : pendingInvitation?.name || t('popup.callItem.unknownCaller')}
          </Text>
          <Text id={callerNumberId} css={styles.callerNumber}>
            {pendingInvitation?.isPageOrIntercom
              ? ''
              : pendingInvitation?.number
                ? toMaskedPhoneNumber(pendingInvitation.number)
                : t('calls.detail.unknownNumber', 'Unknown Number')}
          </Text>
          <audio
            id="incoming-call-tone"
            src={IncomingCallTone}
            autoPlay={shouldRing}
            loop
          />
        </Flex>

        {hasNoActiveCall && (
          <Flex css={styles.buttonRow} {...buttonRowProps}>
            <IncomingCallButton
              variant={IncomingCallButtonVariants.Reject}
              onClick={handleRejectButtonClick}
              allowKeyboardActivation={allowKeyboardActivation}
            >
              {t('incomingCallModal.rejectCall')}
            </IncomingCallButton>

            <IncomingCallButton
              variant={IncomingCallButtonVariants.Accept}
              onClick={handleAcceptButtonClick}
              allowKeyboardActivation={allowKeyboardActivation}
            >
              {t('incomingCallModal.acceptCall')}
            </IncomingCallButton>
          </Flex>
        )}
        {mustRejectOrEndCall && (
          <Flex css={styles.buttonRow} {...buttonRowProps}>
            <IncomingCallButton
              variant={IncomingCallButtonVariants.Reject}
              onClick={handleRejectButtonClick}
              allowKeyboardActivation={allowKeyboardActivation}
            >
              {t('incomingCallModal.rejectCall')}
            </IncomingCallButton>

            <IncomingCallButton
              variant={IncomingCallButtonVariants.EndAndAccept}
              onClick={async () => {
                track(WebPhoneAnalyticsEvents.ModalEndAndAcceptClicked)
                try {
                  await answerCallWithEndCurrent(
                    pendingInvitationUuid || undefined,
                  )
                } catch (error: any) {
                  logger?.error('Failed to answer call with end', error, {
                    pendingInvitationUuid,
                  })
                }
              }}
              allowKeyboardActivation={allowKeyboardActivation}
            >
              {t('incomingCallModal.endAndAccept')}
            </IncomingCallButton>
          </Flex>
        )}
        {canParkOrHoldCall && (
          <Flex css={styles.buttonRow} {...buttonRowProps}>
            <IncomingCallButton
              variant={IncomingCallButtonVariants.HoldAndAccept}
              onClick={async () => {
                track(WebPhoneAnalyticsEvents.ModalHoldAndAcceptClicked)
                try {
                  await answerCallWithHoldCurrent(
                    pendingInvitationUuid || undefined,
                  )
                } catch (error: any) {
                  logger?.error('Failed to answer call with hold', error, {
                    pendingInvitationUuid,
                  })
                }
              }}
              allowKeyboardActivation={allowKeyboardActivation}
            >
              {t('incomingCallModal.holdAndAccept')}
            </IncomingCallButton>

            <ParkCallButton
              variant={IncomingCallButtonVariants.ParkAndAccept}
              onClick={noop}
              onClickParkingLot={handleParkCallButtonClick}
              parkingLots={parkingLots || []}
              allowKeyboardActivation={allowKeyboardActivation}
            >
              {t('incomingCallModal.parkAndAccept')}
            </ParkCallButton>
          </Flex>
        )}
      </Flex>
    </MangoModal>
  )
}
